/**
 * Shared state for a rendered query: mapping, node descriptions, expansion and grouping
 * (spec §15.5–§15.7). Used by the explorer page and the `graph_view` block.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { describeNodes, expandNode, type QueryResult } from "../api/hooks";
import { docIdOf, docIri } from "../lib/iri";
import { mapResults, type MapOptions, type Row } from "./conventionMapper";
import { buildNodeInfo, type NodeDescription } from "./nodeInfo";

export interface ExplorationOptions extends MapOptions {
  groupByContainer?: boolean;
}

/**
 * A stable empty list. A fresh `[]` here made `rows`, `graph` and `info` change identity on every
 * render, so selecting a node re-ran the whole layout with `fit` and `randomize` — the zoom was
 * thrown away and every node position re-scrambled (spec §15.8, maintainer 2026-09-19).
 */
const NO_EXTRA_ROWS: Row[] = [];

export function useGraphExploration(workspaceId: string, result: QueryResult | null, opts: ExplorationOptions) {
  const [extraRows, setExtraRows] = useState<{ for: QueryResult | null; rows: Row[] }>({ for: null, rows: [] });
  const [descriptions, setDescriptions] = useState<NodeDescription[]>([]);
  const [containers, setContainers] = useState<{ key: string; parents: Map<string, string> } | null>(null);

  // expansion rows belong to the result they were added to; a new query starts clean
  const expanded = extraRows.for === result ? extraRows.rows : NO_EXTRA_ROWS;
  const vars = useMemo(() => {
    const v = result?.results.head?.vars ?? [];
    return expanded.length ? [...new Set([...v, "s", "p", "o", "g", "conf"])] : v;
  }, [result, expanded.length]);
  const rows = useMemo(() => [...(result?.results.results?.bindings ?? []), ...expanded] as Row[], [result, expanded]);
  const { showTypeEdges, mergeDerived, maxElements, groupByContainer, staleGraphs } = opts;
  const staleKey = staleGraphs ? [...staleGraphs].sort().join("|") : "";
  const graph = useMemo(
    () =>
      result && result.kind === "bindings"
        ? mapResults(vars, rows, { showTypeEdges, mergeDerived, maxElements, staleGraphs })
        : null,
    // eslint-disable-next-line react-hooks/exhaustive-deps -- staleKey stands in for the set's contents
    [result, vars, rows, showTypeEdges, mergeDerived, maxElements, staleKey],
  );
  const nodeIds = useMemo(() => (graph ? graph.nodes.map((n) => n.id) : []), [graph]);
  const nodeKey = nodeIds.join("|");

  useEffect(() => {
    if (!nodeIds.length) return;
    let stop = false;
    void describeNodes(workspaceId, nodeIds.slice(0, 500))
      .then((d) => !stop && setDescriptions(d))
      .catch(() => {}); // offline: labels fall back to local names
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, nodeKey]);

  useEffect(() => {
    if (!groupByContainer || !nodeIds.length) return;
    let stop = false;
    void api
      .POST("/api/graph/containers", { body: { workspaceId, iris: nodeIds.slice(0, 500) } })
      .then((r) => {
        if (stop || !r.data) return;
        const parents = new Map<string, string>();
        const bindings = (r.data as QueryResult).results.results?.bindings ?? [];
        for (const b of bindings) if (b.s && b.o && b.p?.value.endsWith("inDocument")) parents.set(b.s.value, b.o.value);
        // annotations belong to their page (the IRI embeds the document ID, I6)
        for (const id of nodeIds) {
          if (!parents.has(id) && id.startsWith("urn:km:ann:")) {
            const d = docIdOf(id);
            if (d) parents.set(id, docIri(d));
          }
        }
        setContainers({ key: nodeKey, parents });
      })
      .catch(() => {});
    return () => {
      stop = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspaceId, nodeKey, groupByContainer]);

  const info = useMemo(() => buildNodeInfo(graph, descriptions), [graph, descriptions]);
  const parents = groupByContainer && containers?.key === nodeKey ? containers.parents : undefined;

  const expand = useCallback(
    async (iri: string) => {
      const r = await expandNode(workspaceId, iri, "both", 200);
      const add = (r.results.results?.bindings ?? []) as Row[];
      setExtraRows((prev) => {
        const base = prev.for === result ? prev.rows : [];
        const seen = new Set([...(result?.results.results?.bindings ?? []), ...base].map((x) => `${x.s?.value}|${x.p?.value}|${x.o?.value}|${x.g?.value}`));
        return { for: result, rows: [...base, ...add.filter((x) => !seen.has(`${x.s?.value}|${x.p?.value}|${x.o?.value}|${x.g?.value}`))] };
      });
    },
    [workspaceId, result],
  );

  return { graph, info, parents, expand, incremental: expanded.length > 0 };
}
